import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Divider,
  Tabs,
  Tab,
  useMediaQuery,
  Paper,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  AttachMoney,
  Receipt,
  AccountBalance,
  People,
  RecordVoiceOver,
  Assignment,
  TrendingUp,
  ArrowBack,
  Star,
  AutoAwesome,
} from "@mui/icons-material";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import { NAVBAR_HEIGHT } from "constants/layout";
import { OVERLAY_NAMES } from "constants/Overlays";
import {
  FILTER_SELECTOR_WIDTH,
  FILTER_CONTAINER_GRID,
} from "constants/filterSelectors";
import ReportsFilterSelector from "components/ReportsFilterSelector";
import { CONST } from "constants/CONST";
import dayjs from "dayjs";
import api from "services/api";

// Import report categories
import { FINANCIAL_REPORTS } from "./categories/FinancialReports";
import { BUSINESS_FINANCIAL_REPORTS } from "./categories/BusinessFinancialReports";
import { CLIENT_REPORTS } from "./categories/ClientReports";
import { LINGUIST_REPORTS } from "./categories/LinguistReports";
import { INVOICE_REPORTS } from "./categories/InvoiceReports";
import { SERVICE_OPERATIONS_REPORTS } from "./categories/ServiceOperationsReports";
import { ADVANCED_ANALYTICS_REPORTS } from "./categories/AdvancedAnalyticsReports";

// Report components mapping
const REPORT_COMPONENTS = {
  financial: FINANCIAL_REPORTS,
  invoices: INVOICE_REPORTS,
  expenses: BUSINESS_FINANCIAL_REPORTS,
  clients: CLIENT_REPORTS,
  linguists: LINGUIST_REPORTS,
  services: SERVICE_OPERATIONS_REPORTS,
  analytics: ADVANCED_ANALYTICS_REPORTS,
};

// LocalStorage keys for Reports
const REPORTS_CATEGORY_KEY = "reports_selected_category";
const REPORTS_SELECTED_REPORT_KEY = "reports_selected_report";
const REPORTS_SELECTED_CLIENTS_KEY = "reports_selected_clients";
const REPORTS_SELECTED_LINGUISTS_KEY = "reports_selected_linguists";
const REPORTS_SELECTED_SERVICE_CATEGORIES_KEY =
  "reports_selected_service_categories";
const REPORTS_SELECTED_SERVICE_TYPES_BY_CATEGORY_KEY =
  "reports_selected_service_types_by_category";

const SERVICE_TYPES_BY_CATEGORY = {
  Translation: ["Translation", "Proofreading", "Transcreation"],
  Interpretation: [
    "In-person Interpretation",
    "Virtual Interpretation",
    "OPI",
    "Phone Interpretation",
    "On-Demand Video Interpretation",
    "In-person Simultaneous Interpretation",
    "Virtual Simultaneous Interpretation",
    "In-person or Virtual Interpretation",
    "Interpretation",
  ],
  Other: [
    "Design",
    "Voiceover",
    "Consulting",
    "Retainer Credited",
    "Language Proficiency Testing",
    "Transcription",
    "EQUIPMENT",
    "Other",
    "Honorarium",
    "Captioning",
    "Business",
    "Audio Recording",
    "Virtual Captioning",
    "Interpreter Course",
    "In-person Coordination",
    "PSSA Training",
    "PSTAT Training",
    "Training",
    "Coordination",
    "Speaking Engagement",
  ],
};

// Report categories configuration
const REPORT_CATEGORIES = [
  {
    id: "financial",
    label: "Financial Reports",
    icon: AttachMoney,
    reports: [
      {
        id: "revenue-summary",
        label: "Revenue Summary",
        description:
          "Overall revenue overview showing total income, revenue trends over time, and month-over-month/year-over-year comparisons. Includes breakdowns by service type.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "revenue-by-client",
        label: "Revenue by Client & Segment",
        description:
          "Combines client-level revenue, concentration, and segment performance to identify key accounts and dependency risk.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "revenue-by-service",
        label: "Revenue Mix Analysis",
        description:
          "Unified view of service-type and language revenue mix to show where revenue is generated and where growth is shifting.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "profit-margin",
        label: "Profit & Margin Performance",
        description:
          "Combines gross/net margin trends, service profitability, and pricing efficiency to monitor financial health and margin quality.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "payment-method",
        label: "Payment & Collections",
        description:
          "Consolidates payment method behavior, collection efficiency, and payment timing trends to improve cash conversion.",
        IsAdded: 1,
        IsPrio: 1,
      },
    ],
  },
  {
    id: "invoices",
    label: "Invoice & AR",
    icon: Receipt,
    reports: [
      {
        id: "outstanding-invoices",
        label: "Outstanding Invoices",
        description:
          "List of all unpaid invoices with amounts, due dates, days outstanding, and client information. Critical for cash flow management.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "invoice-aging",
        label: "Aging & Collections",
        description:
          "Combines aging buckets, collection rate, and overdue concentration to prioritize AR follow-up and cash recovery.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "invoice-timeline",
        label: "Invoice Lifecycle",
        description:
          "Tracks invoice creation, due, and payment milestones with paid/unpaid trend context to spot process bottlenecks.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "payment-behavior",
        label: "Client Payment Behavior & Risk",
        description:
          "Combines client-level payment speed, reliability, and late-payment patterns to identify at-risk accounts.",
        IsAdded: 1,
        IsPrio: 1,
      },
    ],
  },
  {
    id: "expenses",
    label: "Business Financial Reports",
    icon: AccountBalance,
    reports: [
      {
        id: "expense-summary",
        label: "Expense Summary & Allocation",
        description:
          "Combines total expense overview with category/subcategory allocation and payment-method distribution.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "expense-trends",
        label: "Expense Trends",
        description:
          "Historical expense analysis showing spending patterns, seasonal variations, and year-over-year expense changes.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "profit-loss",
        label: "Profit & Loss Statement",
        description:
          "Comprehensive P&L showing total revenue minus total expenses, calculating net profit/loss for selected periods with comparative analysis.",
        IsAdded: 1,
        IsPrio: 1,
      },
    ],
  },
  {
    id: "clients",
    label: "Client Reports",
    icon: People,
    reports: [
      {
        id: "client-overview",
        label: "Client Overview",
        description:
          "Dashboard view of key client metrics including Customer Lifetime Value, Revenue Concentration, Client Retention Rate, and Invoice-to-Payment analysis.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "client-list",
        label: "Client Directory & Contracts",
        description:
          "Unified view of client profile data and contract tracking, including status, contract terms, and upcoming renewals.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "client-activity",
        label: "Client Activity & Service Mix",
        description:
          "Combines service volume, last activity, service frequency, service-type usage, and new-client onboarding trends.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "top-clients",
        label: "Client Revenue & Profitability",
        description:
          "Consolidates top-client performance, client profitability, rate consistency, and lifetime value in one financial view.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "client-retention",
        label: "Client Retention & Risk",
        description:
          "Combines retention/churn analysis with risk scoring signals like payment delays, declining activity, and revenue deterioration.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "sla-compliance",
        label: "Client Quality & SLA",
        description:
          "Combines SLA adherence and satisfaction indicators to monitor service quality outcomes across key client accounts.",
        IsAdded: 1,
        IsPrio: 1,
      },
    ],
  },
  {
    id: "linguists",
    label: "Linguist Reports",
    icon: RecordVoiceOver,
    reports: [
      {
        id: "linguist-overview",
        label: "Linguist Overview",
        description:
          "Dashboard view of key linguist metrics including Linguist Retention Rate and performance indicators.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "linguist-directory",
        label: "Linguist Directory & Language Matrix",
        description:
          "Unified profile directory including contact details, languages, proficiency, rates, and availability status.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "compliance-dashboard",
        label: "Compliance & Document Health",
        description:
          "Combines compliance status, document expirations, and background-check requirements into a single risk-focused view.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "linguist-performance",
        label: "Performance & Utilization",
        description:
          "Combines service quality/performance metrics with utilization and workload balance indicators.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "linguist-earnings",
        label: "Payments & Earnings",
        description:
          "Consolidates payment status, outstanding balances, earnings trends, and minimum-hours impact.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "preferred-linguist",
        label: "Client-Linguist Relationship Insights",
        description:
          "Combines preferred-linguist patterns, feedback signals, and assignment consistency by client/account.",
        IsAdded: 1,
        IsPrio: 1,
      },
    ],
  },
  {
    id: "services",
    label: "Service Operations",
    icon: Assignment,
    reports: [
      {
        id: "service-dashboard",
        label: "Service Operations Dashboard",
        description:
          "Unified operations snapshot including service volume, statuses, completion health, and execution KPIs.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "timeline-analysis",
        label: "Scheduling, Timeline & Urgency",
        description:
          "Combines booking lead time, rush activity, cancellation/no-show signals, and operational timeline reliability.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "service-type-distribution",
        label: "Service Mix & Delivery Mode",
        description:
          "Consolidates service types, language-pair demand, and remote vs on-site delivery mix for capacity planning.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "duration-analysis",
        label: "Duration, Effort & Travel",
        description:
          "Combines service duration, word-count effort, mileage/travel, and location load to improve staffing and costing.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "no-show-analysis",
        label: "Service Reliability & Exceptions",
        description:
          "Consolidates cancellations, no-shows, and failure patterns to reduce preventable service disruptions.",
        IsAdded: 1,
        IsPrio: 1,
      },
    ],
  },
  {
    id: "analytics",
    label: "Advanced Analytics",
    icon: TrendingUp,
    reports: [
      {
        id: "business-trends",
        label: "Growth & Business Trends",
        description:
          "Long-range growth analysis combining seasonality, trend movement, and forward-looking performance signals.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "service-mix",
        label: "Service Mix & Efficiency",
        description:
          "Combines service mix optimization with operational efficiency metrics to improve margin-adjusted throughput.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "price-optimization",
        label: "Pricing & Rate Intelligence",
        description:
          "Consolidates internal rate comparison and pricing optimization to improve competitiveness and margin quality.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "demand-forecast",
        label: "Demand, Churn & Forecast",
        description:
          "Combines demand forecasting with churn-risk analytics to support capacity planning and retention strategy.",
        IsAdded: 1,
        IsPrio: 1,
      },
      {
        id: "growth-opportunities",
        label: "Market & Growth Opportunities",
        description:
          "Consolidates cross-sell, market-trend, and opportunity scouting insights to prioritize strategic expansion bets.",
        IsAdded: 1,
        IsPrio: 1,
      },
    ],
  },
];

// Report card wrapper component similar to SettingsCard
const ReportCard = ({ children, theme, sx }) => (
  <Box
    sx={{
      bgcolor: theme.palette.background.default,
      borderRadius: "12px",
      border: `1px solid ${theme.palette.primary.border2}`,
      p: { xs: "0.75rem", sm: "1.25rem" },
      mx: { xs: 0, sm: "0.5rem" },
      mt: "0.25rem",
      boxShadow: theme.shadows[3],
      maxWidth: "100%",
      boxSizing: "border-box",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const Reports = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isMobile = !useMediaQuery("(min-width:600px)");

  const { refreshPage, handleErrorDialog, setOverlayData, setOverlayName } =
    useStateContext();

  // Get saved category and report from localStorage
  const getSavedCategory = () => {
    const saved = localStorage.getItem(REPORTS_CATEGORY_KEY);
    return saved ? parseInt(saved) : 0;
  };

  const getSavedReportId = () => {
    return localStorage.getItem(REPORTS_SELECTED_REPORT_KEY) || null;
  };

  // UI State - using tab index instead of drawer
  const [tabValue, setTabValue] = useState(getSavedCategory);
  const [selectedReportId, setSelectedReportId] = useState(getSavedReportId);

  // Dynamic page title based on selected category
  const currentCategory = REPORT_CATEGORIES[tabValue];
  usePageTitle(
    currentCategory ? `Reports: ${currentCategory.label}` : "Reports",
  );

  // Filter state
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Get saved months from localStorage or use current month as default
  const getSavedMonths = () => {
    const savedMonths = localStorage.getItem(CONST.INTERPRETATION_MONTHS_KEY);
    return savedMonths ? JSON.parse(savedMonths) : [dayjs().format("MMM")];
  };

  const [selectedMonths, setSelectedMonths] = useState(getSavedMonths);
  const [, setTempSelectedMonths] = useState(getSavedMonths);

  // Year selector data
  const currentYear = dayjs().year();

  // Get saved year from localStorage or use current year as default
  const getSavedYear = () => {
    const savedYear = localStorage.getItem(CONST.INTERPRETATION_YEAR_KEY);
    return savedYear ? JSON.parse(savedYear) : [String(currentYear)];
  };

  // Initialize with current year, will be updated when minMaxDates is available
  const [yearOptions, setYearOptions] = useState([String(currentYear)]);

  const [selectedYear, setSelectedYear] = useState(getSavedYear);
  const [, setTempSelectedYear] = useState(getSavedYear);

  const [minMaxDates, setMinMaxDates] = useState();

  // Get saved filters from localStorage
  const getSavedClients = () => {
    const saved = localStorage.getItem(REPORTS_SELECTED_CLIENTS_KEY);
    return saved ? JSON.parse(saved) : [];
  };

  const getSavedLinguists = () => {
    const saved = localStorage.getItem(REPORTS_SELECTED_LINGUISTS_KEY);
    return saved ? JSON.parse(saved) : [];
  };

  const getSavedServiceCategories = () => {
    const saved = localStorage.getItem(REPORTS_SELECTED_SERVICE_CATEGORIES_KEY);
    return saved
      ? JSON.parse(saved)
      : ["Translation", "Interpretation", "Other"];
  };

  const getSavedServiceTypesByCategory = () => {
    const saved = localStorage.getItem(
      REPORTS_SELECTED_SERVICE_TYPES_BY_CATEGORY_KEY,
    );

    if (!saved) {
      // No saved selection — will be replaced with DB types once getDataLists resolves.
      // Fall back to the bootstrap constant so the filter is non-empty on first render.
      return {
        Translation: [...SERVICE_TYPES_BY_CATEGORY.Translation],
        Interpretation: [...SERVICE_TYPES_BY_CATEGORY.Interpretation],
        Other: [...SERVICE_TYPES_BY_CATEGORY.Other],
      };
    }

    const parsed = JSON.parse(saved);
    // When a category is missing from the saved object, use an empty array
    // (DB fetch will fill in any new types via the auto-select logic below).
    return {
      Translation: parsed?.Translation || [],
      Interpretation: parsed?.Interpretation || [],
      Other: parsed?.Other || [],
    };
  };

  // Additional filter states
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState(getSavedClients);
  const [linguists, setLinguists] = useState([]);
  const [selectedLinguists, setSelectedLinguists] = useState(getSavedLinguists);
  const [selectedServiceCategories, setSelectedServiceCategories] = useState(
    getSavedServiceCategories(),
  );
  const [serviceTypesByCategory, setServiceTypesByCategory] = useState(
    SERVICE_TYPES_BY_CATEGORY,
  );
  const [selectedServiceTypesByCategory, setSelectedServiceTypesByCategory] =
    useState(getSavedServiceTypesByCategory);
  const [filtersLoading, setFiltersLoading] = useState(false);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedReportId(null); // Reset report selection when changing tabs
    localStorage.setItem(REPORTS_CATEGORY_KEY, newValue.toString());
    localStorage.removeItem(REPORTS_SELECTED_REPORT_KEY); // Clear selected report when changing category
  };

  // Handle report selection within a category
  const handleReportSelect = (reportId) => {
    setSelectedReportId(reportId);
    localStorage.setItem(REPORTS_SELECTED_REPORT_KEY, reportId);
  };

  // Get current selected category and report
  const getCurrentCategory = () => {
    return REPORT_CATEGORIES[tabValue] || null;
  };

  const getCurrentReport = () => {
    if (!selectedReportId) return null;
    const category = getCurrentCategory();
    if (!category) return null;
    const report = category.reports.find((r) => r.id === selectedReportId);
    return report ? { ...report, category: category.label } : null;
  };

  // Get the report component to render
  const getReportComponent = () => {
    if (!selectedReportId) return null;
    const category = getCurrentCategory();
    if (!category) return null;

    const categoryReports = REPORT_COMPONENTS[category.id];
    if (!categoryReports) return null;

    return categoryReports[selectedReportId] || null;
  };

  // Prepare filters for report components - memoized to prevent unnecessary re-renders
  const reportFilters = useMemo(
    () => ({
      years: selectedYear,
      months: selectedMonths,
      clients: selectedClients,
      linguists: selectedLinguists,
      serviceCategories: selectedServiceCategories,
      translationTypes: selectedServiceTypesByCategory.Translation,
      interpretationTypes: selectedServiceTypesByCategory.Interpretation,
      otherServiceTypes: selectedServiceTypesByCategory.Other,
      serviceTypesByCategory: selectedServiceTypesByCategory,
    }),
    [
      selectedYear,
      selectedMonths,
      selectedClients,
      selectedLinguists,
      selectedServiceCategories,
      selectedServiceTypesByCategory,
    ],
  );

  const openInsightsOverlay = () => {
    const category = getCurrentCategory();
    const report = getCurrentReport();

    setOverlayData({
      source: "reports",
      basePayload: {
        context: {
          generatedAt: new Date().toISOString(),
          categoryId: category?.id || null,
          categoryLabel: category?.label || null,
          selectedReportId: report?.id || null,
          selectedReportLabel: report?.label || null,
          filters: reportFilters,
        },
      },
    });
    setOverlayName(OVERLAY_NAMES.REPORTS_INSIGHTS);
  };

  // Handle filter save from ReportsFilterSelector
  const handleFilterSave = (filters) => {
    // Update state
    setSelectedMonths(filters.months);
    setTempSelectedMonths(filters.months);
    setSelectedYear(filters.years);
    setTempSelectedYear(filters.years);
    setSelectedClients(filters.clients);
    setSelectedLinguists(filters.linguists);
    setSelectedServiceCategories(filters.serviceCategories);
    setSelectedServiceTypesByCategory(filters.serviceTypesByCategory);

    // Save to localStorage
    localStorage.setItem(
      CONST.INTERPRETATION_MONTHS_KEY,
      JSON.stringify(filters.months),
    );
    localStorage.setItem(
      CONST.INTERPRETATION_YEAR_KEY,
      JSON.stringify(filters.years),
    );
    localStorage.setItem(
      REPORTS_SELECTED_CLIENTS_KEY,
      JSON.stringify(filters.clients),
    );
    localStorage.setItem(
      REPORTS_SELECTED_LINGUISTS_KEY,
      JSON.stringify(filters.linguists),
    );
    localStorage.setItem(
      REPORTS_SELECTED_SERVICE_CATEGORIES_KEY,
      JSON.stringify(filters.serviceCategories),
    );
    localStorage.setItem(
      REPORTS_SELECTED_SERVICE_TYPES_BY_CATEGORY_KEY,
      JSON.stringify(filters.serviceTypesByCategory),
    );

    // Fetch data based on filters
    // fetchReportData(filters);
  };

  const getDataLists = useCallback(() => {
    setFiltersLoading(true);
    api
      .all([
        api.get("/dashboard/serviceDateRange"),
        api.get("/clients?limit=10000"),
        api.get("/linguists?limit=10000"),
        api.get("/services/types-by-category"),
      ])
      .then(
        api.spread(
          (
            minMaxDatesResponse,
            clientsResponse,
            linguistsResponse,
            serviceTypesResponse,
          ) => {
            if (minMaxDatesResponse) {
              setMinMaxDates(minMaxDatesResponse.data);
            }
            if (clientsResponse) {
              setClients(
                clientsResponse.data.map((client) => ({
                  id: client.id,
                  label:
                    client.Account || client.ShortName || `Client ${client.id}`,
                })),
              );
            }
            if (linguistsResponse) {
              setLinguists(
                linguistsResponse.data.map((linguist) => ({
                  id: linguist.id,
                  label: linguist.Name || `Linguist ${linguist.id}`,
                })),
              );
            }
            if (serviceTypesResponse) {
              const dbTypes = serviceTypesResponse.data;
              setServiceTypesByCategory(dbTypes);
              // If the user has never saved a custom type selection, populate the
              // selected types from the DB so new/changed subtypes are always included.
              if (!localStorage.getItem(REPORTS_SELECTED_SERVICE_TYPES_BY_CATEGORY_KEY)) {
                setSelectedServiceTypesByCategory({
                  Translation: dbTypes.Translation || [],
                  Interpretation: dbTypes.Interpretation || [],
                  Other: dbTypes.Other || [],
                });
              }
            }
            setFiltersLoading(false);
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
        setFiltersLoading(false);
      });
  }, [handleErrorDialog, theme]);

  useEffect(() => {
    // Initial data fetch
    //fetchInvoicesData();

    // Get all various data lists
    getDataLists();
  }, [refreshPage, getDataLists]); // Don't include fetchInterpretationsData to prevent listener churn

  // Generate year options when minMaxDates changes
  useEffect(() => {
    if (minMaxDates && minMaxDates.startDate && minMaxDates.endDate) {
      const startYear = minMaxDates.startDate.year;
      const endYear = minMaxDates.endDate.year;

      // Create an array of years between startYear and endYear (inclusive)
      const years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(String(year));
      }

      setYearOptions(years);

      // If the current selected year is not in the range, update it to the current year
      // or the closest year in the range
      if (selectedYear.length > 0) {
        const currentSelectedYear = parseInt(selectedYear[0]);
        if (currentSelectedYear < startYear || currentSelectedYear > endYear) {
          // Choose the closest year in range to the current year
          let newYear;
          if (currentYear >= startYear && currentYear <= endYear) {
            newYear = String(currentYear);
          } else if (currentYear < startYear) {
            newYear = String(startYear);
          } else {
            newYear = String(endYear);
          }

          setSelectedYear([newYear]);
          setTempSelectedYear([newYear]);
        }
      }
    }
  }, [minMaxDates, currentYear, selectedYear]);

  return (
    <Box
      p={isMobile ? "1rem 1rem 0 1rem" : "1rem 1rem 0 0"}
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        minHeight: 0,
        height: isMobile ? "auto" : `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      }}
    >
      {/* Header with Filters */}
      <Box sx={{ flexShrink: 0 }}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          pl={isMobile ? 0 : "1rem"}
        >
          {/* Reports Filter Selector */}
          <Grid
            size={FILTER_CONTAINER_GRID}
            sx={{ display: "flex", justifyContent: "flex-start", px: 1 }}
          >
            <Box sx={{ width: "100%", maxWidth: FILTER_SELECTOR_WIDTH }}>
              <ReportsFilterSelector
                months={months}
                years={
                  yearOptions.length > 0 ? yearOptions : [String(currentYear)]
                }
                selectedMonths={selectedMonths}
                selectedYears={selectedYear}
                clients={clients}
                selectedClients={selectedClients}
                linguists={linguists}
                selectedLinguists={selectedLinguists}
                serviceTypesByCategory={serviceTypesByCategory}
                selectedServiceTypesByCategory={selectedServiceTypesByCategory}
                onSave={handleFilterSave}
                loading={filtersLoading}
              />
            </Box>
          </Grid>

          <Grid
            size={{ xs: 12, md: "auto" }}
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-end", md: "flex-end" },
              pr: 1,
            }}
          >
            {!isMobile ? (
              <Button
                onClick={openInsightsOverlay}
                startIcon={<AutoAwesome />}
                sx={{
                  width: "200px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "8px 16px",
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: "12px",
                  boxShadow:
                    "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                  color: theme.palette.background.default,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  "& .MuiButton-startIcon": {
                    marginRight: 0,
                  },
                  "& .MuiButton-endIcon": {
                    display: "none",
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                    boxShadow:
                      "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <Box sx={{ flex: 1, textAlign: "center" }}>Insights</Box>
              </Button>
            ) : (
              <Button
                onClick={openInsightsOverlay}
                sx={{
                  width: "40px",
                  minWidth: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: "12px",
                  boxShadow:
                    "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                  color: theme.palette.background.default,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  "& .MuiButton-startIcon": {
                    margin: 0,
                  },
                  "& .MuiButton-endIcon": {
                    display: "none",
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                    boxShadow:
                      "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <AutoAwesome />
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Main Content with Sidebar Tabs */}
      <Box
        sx={{
          mt: "1rem",
          mx: isMobile ? "0.5rem" : "0rem",
          pb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          flex: 1,
          minHeight: 0,
          overflow: "visible",
        }}
      >
        {/* Sidebar with Tabs */}
        <Box
          sx={{
            minWidth: { xs: "auto", sm: 220 },
            bgcolor: theme.palette.background.default,
            borderRadius: { xs: "12px", sm: "0 12px 12px 0" },
            border: `1px solid ${theme.palette.primary.border2}`,
            p: "0.5rem",
            mt: "0.25rem",
            boxShadow: theme.shadows[3],
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Tabs
            orientation={isXs ? "horizontal" : "vertical"}
            value={tabValue}
            onChange={handleTabChange}
            variant={isXs ? "scrollable" : "standard"}
            scrollButtons={isXs ? "auto" : false}
            allowScrollButtonsMobile={isXs}
            sx={{
              "& .MuiTabs-indicator": {
                left: { xs: "auto", sm: 0 },
                bottom: { xs: 0, sm: "auto" },
                width: { xs: "auto", sm: 3 },
                height: { xs: 3, sm: "auto" },
                borderRadius: { xs: "2px 2px 0 0", sm: "0 2px 2px 0" },
              },
              "& .MuiTabs-scrollButtons": {
                "&.Mui-disabled": {
                  opacity: 0.3,
                },
              },
              "& .MuiTab-root": {
                alignItems: { xs: "center", sm: "flex-start" },
                textAlign: { xs: "center", sm: "left" },
                minHeight: 48,
                px: 2,
                py: 1.5,
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                "&.Mui-selected": {
                  color: theme.palette.primary.main,
                  bgcolor: theme.palette.action.selected,
                  borderRadius: "8px",
                },
                "&:hover": {
                  bgcolor: theme.palette.action.hover,
                  borderRadius: "8px",
                },
              },
            }}
          >
            {REPORT_CATEGORIES.map((category) => (
              <Tab key={category.id} label={category.label} />
            ))}
          </Tabs>
        </Box>

        {/* Main Content Area */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Report Content based on selected tab */}
          <ReportCard
            theme={theme}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "visible",
            }}
          >
            {getCurrentCategory() && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                {/* Category Header */}
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    {selectedReportId ? (
                      <>
                        <IconButton
                          onClick={() => {
                            setSelectedReportId(null);
                            localStorage.removeItem(
                              REPORTS_SELECTED_REPORT_KEY,
                            );
                          }}
                          sx={{
                            color: theme.palette.primary.main,
                            "&:hover": {
                              bgcolor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Tabs
                            value={getCurrentCategory().reports.findIndex(
                              (r) => r.id === selectedReportId,
                            )}
                            onChange={(e, newIndex) => {
                              const newReport =
                                getCurrentCategory().reports[newIndex];
                              if (newReport) {
                                handleReportSelect(newReport.id);
                              }
                            }}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                              minHeight: "40px",
                              "& .MuiTab-root": {
                                minHeight: "40px",
                                py: 1,
                                px: 2,
                                textTransform: "none",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                              },
                              "& .MuiTabs-indicator": {
                                height: 3,
                              },
                            }}
                          >
                            {getCurrentCategory().reports.map((report) => (
                              <Tab
                                key={report.id}
                                label={report.label}
                                sx={{
                                  color:
                                    report.IsAdded === 1
                                      ? theme.palette.primary.text
                                      : theme.palette.primary.text3,
                                  "&.Mui-selected": {
                                    color: theme.palette.primary.text,
                                  },
                                  fontWeight: report.IsPrio === 1 ? 600 : 500,
                                }}
                              />
                            ))}
                          </Tabs>
                        </Box>
                      </>
                    ) : (
                      <>
                        {React.createElement(getCurrentCategory().icon, {
                          sx: {
                            fontSize: 32,
                            color: theme.palette.primary.main,
                          },
                        })}
                        <Box>
                          <Typography variant="h4" fontWeight="bold">
                            {getCurrentCategory().label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getCurrentCategory().reports.length} reports
                            available
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </Box>

                {/* Report List or Selected Report Content */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    p: 1,
                  }}
                >
                  {!selectedReportId ? (
                    // Show list of reports in this category
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: 2,
                        p: 1,
                      }}
                    >
                      {getCurrentCategory().reports.map((report) => (
                        <Paper
                          key={report.id}
                          onClick={() => handleReportSelect(report.id)}
                          sx={{
                            p: 2,
                            cursor: "pointer",
                            bgcolor:
                              report.IsAdded === 1
                                ? "rgba(76, 175, 80, 0.08)"
                                : theme.palette.background.alt,
                            border: `1px solid ${theme.palette.primary.border2}`,
                            borderRadius: "12px",
                            transition: "all 0.2s ease",
                            position: "relative",
                            "&:hover": {
                              bgcolor:
                                report.IsAdded === 1
                                  ? "rgba(76, 175, 80, 0.15)"
                                  : theme.palette.action.hover,
                              borderColor: theme.palette.primary.main,
                              transform: "translateY(-2px)",
                              boxShadow: theme.shadows[4],
                            },
                          }}
                        >
                          {report.IsPrio === 1 && (
                            <Star
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                color: "#FFD700",
                                fontSize: 24,
                                borderRadius: "50%",
                                bgcolor: "rgba(255, 215, 0, 0.1)",
                                p: 0.5,
                              }}
                            />
                          )}
                          <Typography
                            variant="h6"
                            fontWeight="600"
                            gutterBottom
                          >
                            {report.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {report.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    // Show selected report content
                    <Box>
                      {(() => {
                        const ReportComponent = getReportComponent();

                        if (ReportComponent) {
                          // Render the actual report component
                          return <ReportComponent filters={reportFilters} />;
                        } else {
                          // Fallback for reports not yet implemented
                          return (
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                align="center"
                              >
                                Report content for{" "}
                                <strong>{getCurrentReport()?.label}</strong>{" "}
                                will be implemented here.
                              </Typography>

                              <Box sx={{ mt: 3 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  <strong>Applied Filters:</strong>
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  • Years: {selectedYear.join(", ")}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  • Months: {selectedMonths.join(", ")}
                                </Typography>
                                {selectedClients.length > 0 && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    • Clients: {selectedClients.length} selected
                                  </Typography>
                                )}
                                {selectedLinguists.length > 0 && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    • Linguists: {selectedLinguists.length}{" "}
                                    selected
                                  </Typography>
                                )}
                                {selectedServiceCategories.length > 0 && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    • Service Categories:{" "}
                                    {selectedServiceCategories.join(", ")}
                                  </Typography>
                                )}
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  • Service Types:{" "}
                                  {selectedServiceTypesByCategory.Translation
                                    .length +
                                    selectedServiceTypesByCategory
                                      .Interpretation.length +
                                    selectedServiceTypesByCategory.Other
                                      .length}{" "}
                                  selected
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }
                      })()}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </ReportCard>
        </Box>
      </Box>
    </Box>
  );
};

export default Reports;
